import { Node, Edge } from "reactflow";
import { NodeData } from "../types/types";
import { nanoid } from "nanoid";

export interface AIStructureResponse {
  response: string;
  structure?: {
    nodes: Array<Partial<Node<NodeData>>>;
    edges: Array<Partial<Edge>>;
    parentNodeId?: string;
    deleteNodeIds?: string[]; // IDs of nodes to delete
    selectNodeId?: string; // ID of node to select
    formatAll?: boolean; // Trigger layout redistribution (like Cmd+L)
  };
}

export interface ParsedAIResponse {
  conversationalResponse: string;
  nodes: Node<NodeData>[];
  edges: Edge[];
  parentNodeId: string | null;
  deleteNodeIds: string[];
  selectNodeId: string | null;
  formatAll: boolean;
  isValid: boolean;
  errors: string[];
}

/**
 * Parse structured JSON output from OpenAI (guaranteed valid JSON from API)
 * This is used when the API has structured outputs enabled
 */
export function parseStructuredAIResponse(
  structuredResponse: AIStructureResponse,
  existingNodes: Node<NodeData>[] = []
): ParsedAIResponse {
  const result: ParsedAIResponse = {
    conversationalResponse: structuredResponse.response || "",
    nodes: [],
    edges: [],
    parentNodeId: null,
    deleteNodeIds: [],
    selectNodeId: null,
    formatAll: false,
    isValid: true,
    errors: [],
  };

  try {
    if (!structuredResponse.structure) {
      // No structure, just conversational response
      return result;
    }

    const {
      nodes,
      edges,
      parentNodeId,
      deleteNodeIds,
      selectNodeId,
      formatAll,
    } = structuredResponse.structure;

    // Process nodes
    if (nodes && Array.isArray(nodes)) {
      result.nodes = nodes
        .map((nodeData) => {
          return validateAndNormalizeNode(nodeData);
        })
        .filter((node): node is Node<NodeData> => node !== null);
    }

    // Process edges
    if (edges && Array.isArray(edges)) {
      result.edges = edges
        .map((edgeData) => {
          return validateAndNormalizeEdge(edgeData, result.nodes);
        })
        .filter((edge): edge is Edge => edge !== null);
    }

    // Set metadata
    result.parentNodeId = parentNodeId || null;
    result.deleteNodeIds = deleteNodeIds || [];
    result.selectNodeId = selectNodeId || null;
    result.formatAll = formatAll || false;

    console.log(
      `✅ Successfully parsed structured response: ${result.nodes.length} nodes, ${result.edges.length} edges`
    );
  } catch (error) {
    console.error("Error processing structured response:", error);
    result.errors.push(
      `Processing error: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
    result.isValid = false;
  }

  return result;
}

/**
 * Repair common JSON syntax errors
 */
function repairCommonJsonErrors(jsonStr: string): string {
  let repaired = jsonStr;

  // Fix "type": "map" → "type": "mindmap"
  repaired = repaired.replace(/"type"\s*:\s*"map"/g, '"type": "mindmap"');

  // Fix empty property name with position object: "": { "x" → "position": { "x"
  repaired = repaired.replace(/""\s*:\s*{\s*"x"/g, '"position": { "x"');

  // Fix missing comma after "none" before "flagged": "none          "flagged" → "none",\n          "flagged"
  repaired = repaired.replace(
    /"none\s{2,}"flagged"/g,
    '"none",\n          "flagged"'
  );

  // Fix missing colon in edges target: "target "feedback → "target": "feedback
  repaired = repaired.replace(/"target\s+"([^"]+)"/g, '"target": "$1"');

  // Fix missing comma between x and y in position: { "x": 0 "y": 0 } → { "x": 0, "y": 0 }
  repaired = repaired.replace(/(\d+)\s+"y":/g, '$1, "y":');

  // Fix missing opening quote in position: {x": 0 → { "x": 0
  repaired = repaired.replace(/{\s*x":/g, '{ "x":');

  // Fix missing closing quote and comma after parentId: "root        "position" → "root",\n        "position"
  repaired = repaired.replace(
    /"root\s{2,}"position"/g,
    '"root",\n        "position"'
  );

  // Fix empty status value: "status": "", → "status": "none",
  repaired = repaired.replace(/"status"\s*:\s*""\s*,/g, '"status": "none",');

  // Fix missing colon in links: "links [] → "links": []
  repaired = repaired.replace(/"links\s+\[/g, '"links": [');

  // Fix wrong property name: "Properties" → "customProperties"
  repaired = repaired.replace(/"Properties"/g, '"customProperties"');

  // Fix empty property names with various values
  repaired = repaired.replace(/""\s*:\s*"training"/g, '"id": "training"');
  repaired = repaired.replace(/""\s*:\s*"mind"/g, '"type": "mindmap"');
  repaired = repaired.replace(/""\s*:\s*""/g, '"parentId": "root"');

  // Fix missing space in property sequence: "Id": "" → "parentId": "root"
  repaired = repaired.replace(/"Id"\s*:\s*""/g, '"parentId": "root"');

  // Fix missing space before position: position": { → "position": {
  repaired = repaired.replace(/\s+position"\s*:/g, ' "position":');

  // Fix missing comma in edges: "edge-4",source → "edge-4", "source"
  repaired = repaired.replace(/",\s*source:/g, '", "source":');

  // Fix missing colon after property name: "parentId "root" → "parentId": "root"
  repaired = repaired.replace(/"(\w+)"\s+"/g, '"$1": "');

  // Fix missing quotes on boolean/null values: "complete":, → "complete": false,
  repaired = repaired.replace(/"complete"\s*:\s*,/g, '"complete": false,');

  // Fix missing quotes on string values: "flagged":none" → "flagged": "none"
  repaired = repaired.replace(/"flagged"\s*:\s*(\w+)"/g, '"flagged": "$1"');

  // Fix empty property name: "": "mind" → "type": "mindmap"
  repaired = repaired.replace(/""\s*:\s*"mind"/g, '"type": "mindmap"');

  // Fix missing property name in label: "label":Feedback → "label": "Feedback
  repaired = repaired.replace(/"label"\s*:\s*([A-ZÆØÅ])/g, '"label": "$1');

  // Fix missing property name in notes: "notes":p> → "notes": "<p>
  repaired = repaired.replace(/"notes"\s*:\s*p>/g, '"notes": "<p>');

  // Fix missing opening tag: "<>Udnyt → "<p>Udnyt
  repaired = repaired.replace(/"<>([^"]+)<\/p>"/g, '"<p>$1</p>"');

  // Fix missing comma and quote: "#ffffff          "complete" → "#ffffff",\n          "complete"
  repaired = repaired.replace(
    /"(#[0-9a-fA-F]{6})\s{2,}"(\w+)":/g,
    '"$1",\n          "$2":'
  );

  // Fix missing quotes in type: "type":mindmap" → "type": "mindmap"
  repaired = repaired.replace(/"type"\s*:\s*(\w+)"/g, '"type": "$1"');

  // Fix missing quotes before icon: icon": "" → "icon": ""
  repaired = repaired.replace(/\s+icon"\s*:/g, ' "icon":');

  // Fix missing quotes before flagged: flagged": → "flagged":
  repaired = repaired.replace(/\s+flagged"\s*:/g, ' "flagged":');

  // Fix edge ID issues: "id": "-2" → "id": "edge-2"
  repaired = repaired.replace(/"id"\s*:\s*"-(\d+)"/g, '"id": "edge-$1"');

  // Fix empty property name in edges: "": "mindmap" → "type": "mindmap"
  repaired = repaired.replace(
    /{\s*"id":\s*"[^"]+",\s*"source":[^,]+,\s*"target":[^,]+,\s*""\s*:/g,
    (match) => {
      return match.replace(/""\s*:/, '"type":');
    }
  );

  // Fix missing colon in type: "type "mindmap" → "type": "mindmap"
  repaired = repaired.replace(/"type\s+"(\w+)"/g, '"type": "$1"');

  // Fix missing quotes in parentId: :root" → ": "root"
  repaired = repaired.replace(/:\s*root"/g, ': "root"');

  // Fix missing opening bracket for nodes array: "nodes": { → "nodes": [{
  repaired = repaired.replace(/"nodes"\s*:\s*\{/g, '"nodes": [{');

  // Fix missing opening bracket for edges array
  repaired = repaired.replace(/"edges"\s*:\s*\{/g, '"edges": [{');

  // Fix missing quotes before property names like `notes":` → `"notes":`
  repaired = repaired.replace(/\s+(\w+)":/g, ' "$1":');

  // Fix missing commas between object properties
  repaired = repaired.replace(/"\s*\n\s*"/g, '",\n"');

  // Fix missing commas between array elements (objects)
  repaired = repaired.replace(/\}\s*\n\s*\{/g, "},\n{");

  // Fix incomplete hex color values at the end (truncated JSON)
  repaired = repaired.replace(/"#[0-9a-fA-F]{0,5}$/g, '"#ffffff"');

  return repaired;
}

/**
 * Parse AI response and extract structure
 */
export function parseAIStructureResponse(
  aiResponse: string,
  existingNodes: Node<NodeData>[] = []
): ParsedAIResponse {
  const result: ParsedAIResponse = {
    conversationalResponse: "",
    nodes: [],
    edges: [],
    parentNodeId: null,
    deleteNodeIds: [],
    selectNodeId: null,
    formatAll: false,
    isValid: false,
    errors: [],
  };

  try {
    console.log("=== parseAIStructureResponse ===");
    console.log("Full AI Response:", aiResponse);
    console.log("Existing nodes count:", existingNodes.length);

    // Try to extract JSON from the response
    const jsonMatch = aiResponse.match(/\{[\s\S]*\}/);

    if (!jsonMatch) {
      // No JSON found - treat entire response as conversational
      console.log("No JSON found in response");
      result.conversationalResponse = aiResponse;
      result.isValid = true;
      return result;
    }

    // Check if JSON appears to be complete (should end with })
    const jsonStr = jsonMatch[0].trim();
    console.log("Extracted JSON string:", jsonStr);

    // Try to repair common JSON syntax errors before parsing
    let repairedJson = repairCommonJsonErrors(jsonStr);

    let parsed: AIStructureResponse;
    let isIncomplete = false;

    if (!repairedJson.endsWith("}")) {
      console.log("JSON incomplete - doesn't end with }. Attempting to fix...");
      isIncomplete = true;

      // Count opening and closing braces to determine what's missing
      const openBraces = (repairedJson.match(/\{/g) || []).length;
      const closeBraces = (repairedJson.match(/\}/g) || []).length;
      const openBrackets = (repairedJson.match(/\[/g) || []).length;
      const closeBrackets = (repairedJson.match(/\]/g) || []).length;

      // Add missing closing brackets and braces
      const missingBrackets = openBrackets - closeBrackets;
      const missingBraces = openBraces - closeBraces;

      for (let i = 0; i < missingBrackets; i++) {
        repairedJson += "]";
      }
      for (let i = 0; i < missingBraces; i++) {
        repairedJson += "}";
      }

      console.log(
        "Fixed JSON by adding",
        missingBrackets,
        "brackets and",
        missingBraces,
        "braces"
      );

      try {
        parsed = JSON.parse(repairedJson);
        console.log("Successfully parsed fixed JSON");
        console.log(
          "Recovered structure with",
          parsed.structure?.nodes?.length || 0,
          "nodes"
        );
      } catch (parseError) {
        console.error("Failed to parse even after fixing:", parseError);
        console.error(
          "Attempted fixed JSON:",
          repairedJson.substring(0, 500) + "..."
        );

        // Try one more approach - extract just the nodes array if possible
        const nodesMatch = repairedJson.match(/"nodes"\s*:\s*\[([\s\S]*?)\]/);
        if (nodesMatch) {
          try {
            const nodesArray = JSON.parse(`[${nodesMatch[1]}]`);
            console.log(
              "Recovered",
              nodesArray.length,
              "nodes from partial JSON"
            );
            // Create a minimal valid structure
            parsed = {
              response: "Nodes recovered from truncated response",
              structure: {
                nodes: nodesArray,
                edges: [],
                parentNodeId: null,
              },
            };
            isIncomplete = true;
          } catch (nodeParseError) {
            console.error("Could not recover nodes:", nodeParseError);
            result.conversationalResponse =
              "The response was too long and got cut off. Please try asking for fewer items at once (e.g., 2-3 nodes instead of more).";
            result.errors.push("Incomplete JSON response - likely truncated");
            return result;
          }
        } else {
          result.conversationalResponse =
            "The response was too long and got cut off. Please try asking for fewer items at once (e.g., 2-3 nodes instead of more).";
          result.errors.push("Incomplete JSON response - likely truncated");
          return result;
        }
      }
    } else {
      try {
        parsed = JSON.parse(repairedJson);
      } catch (parseError) {
        console.error("JSON parse error:", parseError);
        console.error("Attempted to parse:", repairedJson.substring(0, 500));
        result.conversationalResponse =
          "The AI response had formatting issues. Please try rephrasing your request or ask for fewer nodes at once.";
        result.errors.push(
          `JSON parse error: ${(parseError as Error).message}`
        );
        return result;
      }
    }

    console.log("Parsed JSON:", parsed);

    // Extract conversational response
    result.conversationalResponse = parsed.response || "";

    // If JSON was incomplete, add a note to the response
    if (isIncomplete) {
      result.conversationalResponse +=
        " (Note: Response was truncated but nodes were recovered successfully)";
    }

    // If no structure provided, that's okay - just conversational
    if (!parsed.structure) {
      console.log("No structure in parsed response");
      result.isValid = true;
      return result;
    }

    const {
      nodes,
      edges,
      parentNodeId,
      deleteNodeIds,
      selectNodeId,
      formatAll,
    } = parsed.structure;
    console.log(
      "Structure - nodes:",
      nodes?.length,
      "edges:",
      edges?.length,
      "parentNodeId:",
      parentNodeId,
      "deleteNodeIds:",
      deleteNodeIds?.length || 0,
      "selectNodeId:",
      selectNodeId,
      "formatAll:",
      formatAll
    );

    // Extract special operations
    result.parentNodeId = parentNodeId || null;
    result.deleteNodeIds = deleteNodeIds || [];
    result.selectNodeId = selectNodeId || null;
    result.formatAll = formatAll || false;

    // SAFETY CHECK: Reject requests with excessive node counts
    const MAX_NODES_PER_REQUEST = 25;
    if (nodes && Array.isArray(nodes) && nodes.length > MAX_NODES_PER_REQUEST) {
      console.error(
        `REJECTED: Attempted to create ${nodes.length} nodes (max: ${MAX_NODES_PER_REQUEST})`
      );
      result.conversationalResponse = `⚠️ Cannot create ${nodes.length} nodes at once - this would crash the system! Please structure your content into ${MAX_NODES_PER_REQUEST} or fewer logical categories. For example, a CV should have 5-7 nodes: Profile, Experience, Skills, Education, etc. with details in the notes field.`;
      result.isValid = false;
      result.errors.push(
        `Excessive node count: ${nodes.length} (max: ${MAX_NODES_PER_REQUEST})`
      );
      return result;
    }

    // Validate and normalize nodes
    if (nodes && Array.isArray(nodes)) {
      for (const node of nodes) {
        console.log("Validating node:", node.id, node.data?.label);
        const validatedNode = validateAndNormalizeNode(node);
        if (validatedNode) {
          console.log("Node validated successfully:", validatedNode.id);
          result.nodes.push(validatedNode);
        } else {
          console.error("Node validation failed:", node);
          result.errors.push(`Invalid node structure: ${JSON.stringify(node)}`);
        }
      }
    }

    // Combine existing nodes with newly validated nodes for edge validation
    const allNodes = [...existingNodes, ...result.nodes];

    // Validate and normalize edges
    if (edges && Array.isArray(edges)) {
      for (const edge of edges) {
        console.log("Validating edge:", edge.source, "->", edge.target);
        const validatedEdge = validateAndNormalizeEdge(edge, allNodes);
        if (validatedEdge) {
          console.log("Edge validated successfully:", validatedEdge.id);
          result.edges.push(validatedEdge);
        } else {
          console.error("Edge validation failed:", edge);
          result.errors.push(`Invalid edge structure: ${JSON.stringify(edge)}`);
        }
      }
    }

    // CRITICAL: Fix parentId on all nodes based on validated edges
    // This ensures parentId matches edge.source for consistency
    for (const node of result.nodes) {
      const incomingEdge = result.edges.find((e) => e.target === node.id);
      if (incomingEdge) {
        const correctParentId = incomingEdge.source;

        // Validate no circular reference
        if (correctParentId === node.id) {
          console.error(
            `Circular reference detected: node ${node.id} has itself as parent`
          );
          result.errors.push(
            `Node "${node.data.label}" cannot be its own parent`
          );
          continue;
        }

        if (node.parentId !== correctParentId) {
          console.log(
            `Correcting parentId for node "${node.data.label}" from "${node.parentId}" to "${correctParentId}"`
          );
          node.parentId = correctParentId;
        }
      } else if (!node.parentId || node.parentId === "") {
        // Node without edge and without parentId - likely a root-level node update
        console.log(
          `Node "${node.data.label}" has no incoming edge - treating as update to existing node`
        );
      } else {
        console.warn(
          `No incoming edge found for node "${node.data.label}" (${node.id}), keeping parentId: ${node.parentId}`
        );
      }
    }

    result.isValid = result.errors.length === 0;
    console.log(
      "Final result - nodes:",
      result.nodes.length,
      "edges:",
      result.edges.length,
      "errors:",
      result.errors.length
    );
    console.log("=== End parseAIStructureResponse ===");
    return result;
  } catch (error) {
    const errorMsg = (error as Error).message;

    result.errors.push(`Failed to parse AI response: ${errorMsg}`);

    // If it's a JSON parse error, likely the response was truncated
    if (errorMsg.includes("JSON") || errorMsg.includes("Unexpected")) {
      result.conversationalResponse =
        "The response was too large and got cut off. Please try asking for fewer nodes at once (e.g., 2-3 nodes instead of more).";
    } else {
      result.conversationalResponse =
        "Sorry, I couldn't process that response. Please try a simpler request.";
    }

    return result;
  }
}

/**
 * Validate and normalize a node to ensure it has all required fields
 */
function validateAndNormalizeNode(
  node: Partial<Node<NodeData>>
): Node<NodeData> | null {
  try {
    // Required fields
    if (!node.data?.label) {
      return null;
    }

    // Generate ID if not provided
    const id = node.id || nanoid();

    // Handle type correctly: ReactFlow types vs semantic types
    // Semantic types belong in data.type, not node.type
    const semanticTypes = [
      "task",
      "resource",
      "location",
      "risk",
      "decision",
      "image",
    ];
    let nodeType = node.type || "mindmap";
    let semanticType = node.data.type;

    // If AI mistakenly put semantic type as node.type, move it to data.type
    if (semanticTypes.includes(nodeType)) {
      console.log(
        `Moving semantic type "${nodeType}" from node.type to data.type`
      );
      semanticType = nodeType as any;
      nodeType = "mindmap";
    }

    // Default position
    const position = node.position || { x: 0, y: 0 };

    // Initialize task object if this is a task type node
    let taskData = node.data.task || ({} as any);
    if (semanticType === "task" && !node.data.task?.id) {
      taskData = {
        id: id,
        title: node.data.label,
        nodeTitle: node.data.label,
        status: node.data.status || "Open",
        summary: node.data.notes || "",
        priority: node.data.priority || "Medium",
        tags: "",
        assignee: "",
        estimate: 0,
        rankId: 0,
        color: node.data.color || "black",
        className: "",
        startDate: undefined,
        endDate: undefined,
      };
      console.log(
        `Initialized task object for node "${node.data.label}" with id: ${id}`
      );
    }

    // Normalize data with all required fields
    const data: NodeData = {
      label: node.data.label,
      type: semanticType, // Use corrected semantic type (task, resource, etc.)
      notes: node.data.notes || "",
      icon: node.data.icon || "",
      object: node.data.object || "",
      objectType: node.data.objectType || "",
      objectWidth: node.data.objectWidth || 50,
      objectHeight: node.data.objectHeight || 18,
      complete: node.data.complete || false,
      status: node.data.status || "none",
      flagged: node.data.flagged || "none",
      color: node.data.color || "black",
      backgroundColor: node.data.backgroundColor || "#ffffff",
      presentationOrder: node.data.presentationOrder || "",
      priority: node.data.priority || "",
      links: node.data.links || [],
      value: node.data.value || "0",
      values: node.data.values || [],
      rollupValues: node.data.rollupValues || [],
      customProperties: node.data.customProperties || [],
      event: node.data.event || ({} as any),
      address: node.data.address || ({} as any),
      task: taskData,
      ms365Document: node.data.ms365Document || null,
    };

    // Construct complete node
    const completeNode: Node<NodeData> = {
      id,
      type: nodeType, // Use corrected ReactFlow type (mindmap, center, image)
      position,
      positionAbsolute: position,
      data,
      dragging: false,
      selected: false,
      width: data.objectWidth,
      height: data.objectHeight,
      dragHandle: ".dragHandle",
      parentId: node.parentId || "",
      style: node.style || { backgroundColor: "#fff" },
    };

    return completeNode;
  } catch (error) {
    console.error("Error validating node:", error);
    return null;
  }
}

/**
 * Validate and normalize an edge
 * Edge type should be "center" if source is root or a center node
 */
function validateAndNormalizeEdge(
  edge: Partial<Edge>,
  nodes: Node<NodeData>[]
): Edge | null {
  try {
    // Required fields
    if (!edge.source || !edge.target) {
      return null;
    }

    // Always generate a new unique ID to avoid collisions
    // AI-generated IDs like "edge1", "edge2" are not unique across the entire map
    const id = nanoid();

    // Determine edge type based on source node
    const sourceNode = nodes.find((n) => n.id === edge.source);
    let type = edge.type || "mindmap";

    // Use "center" type if source is root or a center node
    if (
      edge.source === "root" ||
      sourceNode?.type === "center" ||
      sourceNode?.id === "root"
    ) {
      type = "center";
    }

    // Construct complete edge
    const completeEdge: Edge = {
      id,
      source: edge.source,
      target: edge.target,
      type,
    };

    return completeEdge;
  } catch (error) {
    console.error("Error validating edge:", error);
    return null;
  }
}

/**
 * Calculate non-overlapping positions for new nodes around parent
 * This follows the same logic as getChildNodePosition in flow.tsx
 * Positions are relative to parent node's center
 */
export function calculateChildNodePositions(
  nodes: Node<NodeData>[],
  parentNodeId: string,
  newNodeCount: number,
  spacing: number = 180
): Array<{ x: number; y: number }> {
  const positions: Array<{ x: number; y: number }> = [];

  // Find the parent node to get its dimensions
  const parentNode = nodes.find((n) => n.id === parentNodeId);

  // Get existing children of this parent
  const existingChildren = nodes.filter((n) => n.parentId === parentNodeId);

  // Calculate angles of existing children to avoid overlaps
  const existingAngles = existingChildren.map((child) => {
    const dx = child.position.x;
    const dy = child.position.y;
    return Math.atan2(dy, dx) * (180 / Math.PI);
  });

  // Function to check if an angle is too close to existing angles
  const isTooClose = (angle: number, minDistance: number = 30): boolean => {
    return existingAngles.some((existingAngle) => {
      let diff = Math.abs(angle - existingAngle);
      // Handle wrap-around at 360/0 degrees
      if (diff > 180) diff = 360 - diff;
      return diff < minDistance;
    });
  };

  // Calculate positions in a circular pattern, avoiding existing nodes
  const totalSlots = existingChildren.length + newNodeCount;
  const angleStep = 360 / Math.max(totalSlots, 8); // Minimum 8 slots for good distribution

  let currentAngle = 0;
  let attempts = 0;
  const maxAttempts = 360;

  for (let i = 0; i < newNodeCount && attempts < maxAttempts; attempts++) {
    // Try angles starting from current position
    if (!isTooClose(currentAngle)) {
      const angleRad = (currentAngle * Math.PI) / 180;

      // Calculate position relative to parent center
      const x = Math.cos(angleRad) * spacing;
      const y = Math.sin(angleRad) * spacing;

      positions.push({ x, y });
      i++;
    }

    // Move to next angle
    currentAngle = (currentAngle + angleStep) % 360;
  }

  // If we couldn't find enough non-overlapping positions, use larger radius
  if (positions.length < newNodeCount) {
    console.warn(
      "Could not find enough non-overlapping positions, using larger radius"
    );
    const largerSpacing = spacing * 1.5;

    for (let i = positions.length; i < newNodeCount; i++) {
      const angle = (i * (360 / newNodeCount) * Math.PI) / 180;
      const x = Math.cos(angle) * largerSpacing;
      const y = Math.sin(angle) * largerSpacing;
      positions.push({ x, y });
    }
  }

  return positions;
}
